import React, { useMemo } from "react";
import { Row } from "antd";
import { Payment } from "../../../../lib/types/Payment";
import { DataItem } from "../../../../components/common/DataItem/DataItem";
import { useAppSelector } from "../../../..";
import { getDictionaryItemById, round, toDisplayFormat } from "../../../../lib/utils";
import { Adjustment } from "../../../../lib/types/Adjustment";
import "./AccountPaymentDetails.less";

interface ComponentProps {
  payment: Payment | undefined;
  paymentAdjustments: Adjustment[] | undefined;
}

const dataItemProps: Partial<Parameters<typeof DataItem>[0]> = {
  wraperColProps: {
    span: 8,
  },
  labelColProps: {
    span: 7,
  },
  valueColProps: {
    span: 17,
  },
  tooltipProps: {
    overlayClassName: "account-payment-details__title",
    placement: "topLeft",
  },
};

export const AccountPaymentDetails = (props: ComponentProps) => {
  const { payment, paymentAdjustments } = props;

  const { paymentStatuses, paymentSourceTypes } = useAppSelector((state) => state.rtkDictionaries);

  const paymentData = useMemo(() => {
    if (!payment) {
      return undefined;
    }

    return {
      ...payment,
      agreementId: payment?.agreement.id,
      paymentDate: payment.paymentDate ? toDisplayFormat(payment.paymentDate) : "",
      confirmDate: payment.confirmDate ? toDisplayFormat(payment.confirmDate) : "",
      amount: round(payment.amount),
      goalsAmount: payment?.goals,
      periodBill: payment?.goals,
      amountBill: payment?.goals,
      amountLeftBill: payment?.goals,
      agreementNumber: payment?.agreement?.agreementNumber,
      reserveLeftAmount: payment?.extra?.reserveLeftAmount,
      usedAmount: payment?.extra?.usedAmount,
      availableAmount: payment?.extra?.availableAmount,
      hasAdjusments: !!paymentAdjustments?.length,
      sourceTypeName: paymentSourceTypes.find(({ id }) => id === payment.sourceTypeId)?.name,
      documentNumber: payment?.info?.documentNumber,
      paymentStatusName: getDictionaryItemById(paymentStatuses, payment.paymentStatusId)?.name,
      purpose: payment?.info?.purpose,
      infoArray: Object.entries(payment?.info || {}).map(([label, value]) => ({ label, value })),
    };
  }, [payment, paymentAdjustments, paymentStatuses]);

  if (!paymentData) {
    return null;
  }

  return (
    <div className="account-payment-details">
      <Row>
        <DataItem label="Договор" value={paymentData.agreementNumber} {...dataItemProps} />
        <DataItem
          label="Сумма"
          value={paymentData.amount}
          className={paymentData.hasAdjusments ? "account-payment-details__hasAdjusments" : ""}
          {...dataItemProps}
        />
        <DataItem label="Источник платежа" value={paymentData.sourceTypeName} {...dataItemProps} />
      </Row>
      <Row>
        <DataItem label="Дата платежа" value={paymentData.paymentDate} {...dataItemProps} />
        <DataItem label="Резерв" value={paymentData.reserveLeftAmount} {...dataItemProps} />
        <DataItem label="Номер документа" value={paymentData.documentNumber} {...dataItemProps} />
      </Row>
      <Row>
        <DataItem label="Дата обработки" value={paymentData.confirmDate} {...dataItemProps} />
        <DataItem label="Использовано" value={paymentData.usedAmount} {...dataItemProps} />
        <DataItem
          label="Назначение"
          value={paymentData.purpose}
          {...dataItemProps}
          tooltipTitle={paymentData.purpose}
        />
      </Row>
      <Row>
        <DataItem label="Статус" value={paymentData.paymentStatusName} {...dataItemProps} />
        <DataItem label="Остаток" value={paymentData.availableAmount} {...dataItemProps} />
        <DataItem
          label="Спец. инф."
          value={
            <div className="account-payment-details__info">
              {paymentData.infoArray.map(({ label, value }) => `${label}: "${value}"`).join(", ")}
            </div>
          }
          {...dataItemProps}
          tooltipTitle={
            <ul>
              {paymentData.infoArray.map(({ label, value }) => (
                <li key={label}>{`${label}: ${value}`}</li>
              ))}
            </ul>
          }
        />
      </Row>
    </div>
  );
};
